import { Component, HostListener, Injector, OnInit, afterNextRender, inject, ChangeDetectionStrategy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { SmartAlertsApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/smart-alerts/smart-alerts-api';
import { DOCUMENT, registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import localeNl from '@angular/common/locales/nl';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeEs from '@angular/common/locales/es';
import { AppLoaderComponent } from './@commons/@components/loaders/app-loader/app-loader.component';
import { StorageService } from './@commons/@services/front/storage.service';
import { UserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-api';
import { PosthogService } from './@commons/@services/front/posthog.service';
import { DeviceTokenService } from './@commons/@services/webview/device-token.service';
import { PremiumService } from './@commons/@services/front/premium.service';
import { PremiumCelebrationComponent } from './@commons/ui-components/premium-celebration/premium-celebration.component';
import { SessionOfflineScreenComponent } from './@commons/@components/session-offline/session-offline-screen.component';
import { NativeBridgeService } from './@commons/@services/webview/native-bridge.service';
import { AppConfigService } from './@commons/@services/front/app-config.service';
import { AppUpdateScreenComponent } from './@commons/@components/app-update/app-update-screen.component';
import { BackHandlerService } from './@commons/@services/webview/back-handler.service';
import { PushPermissionToastComponent } from './@commons/@toasts/push-permission-toast/push-permission-toast.component';
import { PushBannerComponent } from './@commons/@components/push-banner/push-banner.component';

// Enregistre les langues nécessaires;

registerLocaleData(localeFr);
registerLocaleData(localeEn);
registerLocaleData(localeNl);
registerLocaleData(localeDe);
registerLocaleData(localeIt);
registerLocaleData(localeEs);

@Component({
  selector: 'vw-root',
  imports: [
    RouterOutlet,
    AppLoaderComponent,
    PremiumCelebrationComponent,
    SessionOfflineScreenComponent,
    PushPermissionToastComponent,
    PushBannerComponent,
    AppUpdateScreenComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: UserApi,
      useClass: UserApi,
    },
    {
      provide: SmartAlertsApi,
      useClass: SmartAlertsApi,
    },
  ],
})
export class AppComponent implements OnInit {
  private static readonly APP_OPEN_THROTTLE_MS = 30 * 60 * 1000; // 30 min

  /** Alert uids already attributed this session — avoids re-firing markAsOpened on re-navigations. */
  private readonly trackedOpenedAlerts = new Set<string>();

  readonly premiumService = inject(PremiumService);
  private readonly authApi = new AuthApi();
  private readonly nativeBridge = inject(NativeBridgeService);
  /** Versions minimale et recommandée de la coque (`/app-config`). */
  private readonly appConfig = inject(AppConfigService);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  /**
   * Installe `window.vwBack` dès le démarrage : le retour du téléphone ferme
   * d'abord la feuille ouverte (coque 1.5.1).
   */
  readonly backHandler = inject(BackHandlerService);

  /**
   * Un essai n'est pas un achat : dire « merci pour ton abonnement » à qui vient
   * de cliquer « essayer » sonne faux, et laisse croire qu'il a payé. Les mois
   * offerts (`MANUAL`) relèvent de la même prudence.
   */
  isTrialActivation(): boolean {
    const provider = this.premiumService.subscription()?.provider;
    return provider === 'TRIAL' || provider === 'MANUAL';
  }

  constructor(
    private translate: TranslateService,
    private storageService: StorageService,
    private posthogService: PosthogService,
    private deviceTokenService: DeviceTokenService,
    private userApi: UserApi,
    private router: Router,
    private smartAlertsApi: SmartAlertsApi,
  ) {
    const availableLanguages = ['fr', 'en', 'nl', 'de', 'it', 'es'];
    this.translate.addLangs(availableLanguages);
    this.translate.setFallbackLang('fr');
    // Récupérer la langue stockée (si existante)
    const storedLang = this.storageService.getCurrentLanguage()?.toLowerCase();
    // Détecter la langue du navigateur
    const browserLang = navigator.language.split('-')[0]; // Ex: "en-US" → "en"

    // Vérifier si la langue stockée est valide, sinon utiliser celle du navigateur, sinon "fr"
    const userLang = availableLanguages.includes(storedLang)
      ? storedLang
      : availableLanguages.includes(browserLang)
        ? browserLang
        : 'fr';

    console.log(`Langue choisie : ${userLang}`);

    // Stocker la langue sélectionnée pour éviter de la redétecter à chaque fois
    this.storageService.setCurrentLang(userLang.toUpperCase());

    // `<html lang>` suit la langue de l'app, à chaque changement (R8) : lecteur
    // d'écran, césure et traduction automatique du navigateur s'y fient.
    this.translate.onLangChange.subscribe(({ lang }) => (this.document.documentElement.lang = lang));

    // Appliquer la langue choisie
    this.translate.use(userLang);
  }

  ngOnInit(): void {
    this.signalAppReady();
    void this.appConfig.load();
    // Les jetons vivent dans le localStorage de la WebView : rien à attendre du
    // natif. (L'ancienne relecture `readValue` n'avait aucune réponse côté
    // Flutter et bloquait ces trois appels pour toujours.)
    this.trackAppOpen();
    void this.premiumService.init();
    // A push tapped from a cold start lands us on a deep-link carrying ?alertUid=…
    this.trackOpenedAlertFromUrl(this.router.url);

    // In-session navigations (push tapped while the app is already open) also carry the param.
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        this.trackOpenedAlertFromUrl(event.urlAfterRedirects);
        // Le garde a laissé passer : le jeton est valide, l'état premium peut
        // être lu (sans appel s'il est déjà connu pour ce compte).
        void this.premiumService.ensureServerState();
        // Au démarrage à froid, le jeton relu est souvent expiré : l'ouverture
        // n'est comptée qu'une fois le garde passé (le délai de 30 min évite
        // de compter chaque navigation).
        this.trackAppOpen();
        // Jeton push reçu avant la session (nouvel installé) : la première
        // navigation avec une session, qu'elle vienne d'une connexion, de
        // l'entrée invité, d'une inscription ou de la fin de l'onboarding,
        // l'enregistre. Sans jeton en attente, ne fait rien.
        void this.deviceTokenService.registerPendingToken();
      });
  }

  /**
   * Dit à la coque native que le premier écran est peint, pour qu'elle retire
   * son écran de lancement sans laisser voir de blanc. Après la première
   * navigation, qu'elle aboutisse ou non (session hors ligne, refus du garde) :
   * l'écran « Pas de connexion » doit se voir aussi.
   */
  private signalAppReady(): void {
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        take(1),
      )
      .subscribe(() =>
        afterNextRender(() => this.nativeBridge.appReady(), {
          injector: this.injector,
        }),
      );
  }

  /**
   * Reads the `alertUid` query param from a deep-link URL and records the push OPEN
   * (analytics). De-duplicated per session and fire-and-forget: a failure (e.g. the
   * token is not hydrated yet, or the user is anonymous) must never break navigation.
   */
  private trackOpenedAlertFromUrl(url: string): void {
    const queryIndex = url.indexOf('?');
    if (queryIndex === -1) {
      return;
    }
    const alertUid = new URLSearchParams(url.slice(queryIndex + 1)).get(
      'alertUid',
    );
    if (!alertUid || this.trackedOpenedAlerts.has(alertUid)) {
      return;
    }
    this.trackedOpenedAlerts.add(alertUid);
    this.smartAlertsApi.markAsOpened(alertUid).catch(() => {});
  }

  /**
   * Records an app open (cold start or foreground resume) for the logged-in user.
   * Throttled to 30 min so internal navigations / quick refreshes don't count.
   * Fire-and-forget: a failure must never break the UX.
   */
  private trackAppOpen(): void {
    // Anonymous / pre-login opens are out of scope; an expired token would end
    // in a 401 and still burn the throttle: wait for the guard to renew it.
    if (!this.authApi.hasValidToken()) {
      return;
    }
    const last = Number(localStorage.getItem('last_app_open_tracked') || 0);
    if (Date.now() - last < AppComponent.APP_OPEN_THROTTLE_MS) {
      return;
    }
    localStorage.setItem('last_app_open_tracked', String(Date.now()));
    this.userApi.appOpen().catch(() => {});
    this.posthogService.capture('app_open', { channel: this.nativeBridge.platform });
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    // The Flutter WebView is reused across resumes, so visibilityState flips to
    // 'visible' on native foreground — this covers mobile without touching Flutter.
    if (document.visibilityState === 'visible') {
      this.trackAppOpen();
    }
  }
}
